import { useMemo, useState } from 'react';

import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';
import { WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';
import { useQueryState } from 'hooks/useQueryState';

import { useQueryRiskDensityBy } from 'api/queries/riskDensityBy.query';

import { TDateRangeOption } from 'components/common/CustomFilters/TableDateRangeFilter/TableDateRangeFilter';
import { getInputLabel } from 'components/common/CustomFilters/TableDateRangeFilter/TableDateRangeFilter.utils';
import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import {
    SelectByOptionsValue,
    resolutionOptions,
    selectByOptions,
} from 'components/widgets/RiskDensity/RiskDensity.utils';

export const useRiskDensityProvider = ({
    widgetId,
    contractId,
    projectId,
    latestVersionId,
    globalFilterData,
    localFilters,
    widgetViewMode,
    componentKey,
}) => {
    const { isGenerativeAi } = usePinboardWidgetWrapperContext();
    const isInPinboard = Boolean(widgetId);

    const [startProjectDate, setStartProjectDate] = useState<number | null>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.startProjectDate : null,
    );

    const [endProjectDate, setEndProjectDate] = useState<number | null>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.endProjectDate : null,
    );
    const [dateRange, setDateRange] = useState<{ startDate: number | null; endDate: number | null }>({
        startDate: WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.dateRange?.startDate : null,
        endDate: WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.dateRange?.endDate : null,
    });
    const [dateRangeOption, setDateRangeOption] = useState<TDateRangeOption>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.dateRangeOption : 'wholeProject',
    );
    const [resolution, setResolution] = useState<boolean>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters.resolution : false,
    );

    const [riskDensityBy, setRiskDensityBy] = useQueryState(
        'riskDensityBy',
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.riskDensityBy : 'heatMapActivityStarts',
    );
    const [selectBy, setSelectBy] = useState<SelectByOptionsValue | null>(
        riskDensityBy ? selectByOptions.find((filter) => filter.value === riskDensityBy)?.value || null : null,
    );

    const source = isInPinboard
        ? localFilters
        : {
              ...globalFilterData,
              dateRange,
              resolution,
              startProjectDate,
              endProjectDate,
              dateRangeOption,
              riskDensityBy,
              selectBy,
          };

    const filters = useMemo(
        () =>
            getFiltersData({
                [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                    ?.filter((item) => !item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                    ?.filter((item) => item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
                [FilterNames.TAG_CARD_BOARD]: source?.tags
                    ?.filter((item) => !item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags
                    ?.filter((item) => item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.WBS_SEARCH]: source?.wbs,
            }),

        [source?.activityCodes, source?.milestoneFragnet, source?.tags, source?.wbs, globalFilterData],
    );

    const pillsData = {
        wbs: source?.wbs,
        milestoneFragnet: source?.milestoneFragnet,
        activityCodes: source?.activityCodes?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        tags: source?.tags?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        tagsExclude: source?.tags?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        resolution: resolutionOptions.find((item) => item.value === source?.resolution)?.label,
        dateRange: getInputLabel(source?.dateRange),
    };

    const { pillsList: pillsFromHook } = useGlobalFiltersHook({ widgetId, source: pillsData, latestVersionId });
    const pills = useMemo(() => pillsFromHook, [JSON.stringify(pillsFromHook)]);

    const payload = useMemo(() => {
        return {
            id: latestVersionId ? [latestVersionId] : [],
            filter: {
                filters,
            },
            startDate: source?.dateRange?.startDate,
            endDate: source?.dateRange?.endDate,
            isWeekResolution: source?.resolution,
            selectBy: source?.selectBy,
            ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId),
        };
    }, [latestVersionId, filters, source?.dateRange, source?.resolution, source?.selectBy, isGenerativeAi]);

    const state = {
        project: {
            projectId,
            contractId,
            latestVersionId,
        },
        globalFilterData: {
            activityCodes: source?.activityCodes,
            milestoneFragnet: source?.milestoneFragnet,
            tags: source?.tags,
            wbs: source?.wbs,
        },
        local: {
            dateRange: source?.dateRange,
            resolution: source?.resolution,
            startProjectDate: source?.startProjectDate,
            endProjectDate: source?.endProjectDate,
            dateRangeOption: source?.dateRangeOption,
            riskDensityBy: source?.riskDensityBy,
            selectBy: source?.selectBy,
        },
    };

    return {
        ui: {
            state: {
                ...state.project,
                ...state.globalFilterData,
                ...state.local,
            },
            isInPinboard: Boolean(widgetId),
            pills,
        },
        api: {
            riskDensityBy: {
                instance: useQueryRiskDensityBy(payload),
                payload: payload,
            },
        },
        setStartProjectDate,
        setEndProjectDate,
        setDateRange,
        setDateRangeOption,
        setRiskDensityBy,
        setResolution,
        setSelectBy,
    };
};
